/**
 * AGV 3D Factory
 *
 * 범퍼 + 본체 + 플랫폼 3단 구조:
 *   - 하단 범퍼 (10%, 본체보다 약간 넓음, #222222)
 *   - 본체 (58%, RoundedBoxGeometry, statusColor 적용)
 *   - 간격 (4%)
 *   - 상단 플랫폼 (28%, 본체보다 안쪽, #333333)
 *
 * Based on operato-scene Vehicle3D, adapted for fmsim:
 *   - extends RealObjectGroup (Conveyor3D와 동일 패턴)
 *   - statusColor from MCSStatusMixin
 *   - 볼륨 중심 원점 (things-scene 기본 컨벤션) - mesh 로컬 [-d/2, +d/2]
 */

import * as THREE from 'three'
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js'
import { RealObjectGroup, registerRealObjectFactory, disposeObject3D, MaterialBank } from '@hatiolab/things-scene'
import type { Component } from '@hatiolab/things-scene'
import { TRANSPORT_SURFACE_HEIGHT } from './machine-3d.js'

const BUMPER_COLOR = 0x222222
const PLATFORM_COLOR = 0x333333
const DEFAULT_BODY_COLOR = 0x6688cc

export class AGV3D extends RealObjectGroup {
  private _ownMeshes: THREE.Mesh[] = []
  get effectiveDepth(): number {
    const { depth } = this.component.state
    return depth || TRANSPORT_SURFACE_HEIGHT
  }

  private resolveColor(): number {
    const comp = this.component as any

    // 1) MCSStatusMixin.statusColor (legend 기반 상태 색상)
    //    '#F0F0F0'은 상태 미설정 시 기본 fallback — 무시하고 defaultColor 사용
    try {
      const sc = comp.statusColor
      if (typeof sc === 'string' && sc && sc.toUpperCase() !== '#F0F0F0' && sc !== 'transparent') return new THREE.Color(sc).getHex()
    } catch {
      /* statusColor 미구현 시 무시 */
    }

    // 2) state.fillStyle fallback
    const { fillStyle } = this.component.state
    if (fillStyle && typeof fillStyle === 'string' && fillStyle !== 'transparent') {
      try {
        return new THREE.Color(fillStyle).getHex()
      } catch {
        /* 파싱 실패 시 무시 */
      }
    }

    return DEFAULT_BODY_COLOR
  }

  build() {
    super.build()

    const { width = 10, height = 10 } = this.component.state
    const w = Math.abs(width)
    const h = Math.abs(height)
    const d = this.effectiveDepth
    if (!w || !h) return

    // 비율: 하단 범퍼 10%, 본체 58%, 간격 4%, 플랫폼 28%
    const bumperH = d * 0.1
    const bodyH = d * 0.58
    const gapH = d * 0.04
    const platH = d * 0.28

    const cornerR = Math.min(w, h, bodyH) * 0.12

    // ── 하단 범퍼 밴드 (본체보다 약간 넓게 돌출) ──
    const bumperPad = Math.min(w, h) * 0.03
    const bumperCornerR = Math.min(w + bumperPad * 2, h + bumperPad * 2, bumperH) * 0.2
    const bumperGeo = new RoundedBoxGeometry(w + bumperPad * 2, bumperH, h + bumperPad * 2, 4, bumperCornerR)
    // MaterialBank 공유 — dedup + faint 안전(코어 변형 스왑, disposeObject3D 가 __bankOwned 스킵).
    const bumperMat = MaterialBank.material('agv|bumper', { roughness: 0.8, metalness: 0.1 }, BUMPER_COLOR)
    const bumper = new THREE.Mesh(bumperGeo, bumperMat)
    bumper.position.y = bumperH / 2
    bumper.castShadow = true
    bumper.receiveShadow = true

    // ── 본체 (둥근 모서리, statusColor 적용) ──
    const bodyColor = this.resolveColor()
    const bodyGeo = new RoundedBoxGeometry(w, bodyH, h, 4, cornerR)
    const bodyMat = MaterialBank.material('agv|body', { roughness: 0.3, metalness: 0.5 }, bodyColor)
    const body = new THREE.Mesh(bodyGeo, bodyMat)
    body.position.y = bumperH + bodyH / 2
    body.castShadow = true
    body.receiveShadow = true

    // ── 상단 플랫폼 (본체보다 안쪽, 어두운색) ──
    const platInset = Math.min(w, h) * 0.04
    const platCornerR = Math.min(w - platInset * 2, h - platInset * 2, platH) * 0.15
    const platGeo = new RoundedBoxGeometry(w - platInset * 2, platH, h - platInset * 2, 4, platCornerR)
    const platMat = MaterialBank.material('agv|platform', { roughness: 0.5, metalness: 0.15 }, PLATFORM_COLOR)
    const plat = new THREE.Mesh(platGeo, platMat)
    plat.position.y = bumperH + bodyH + gapH + platH / 2
    plat.castShadow = true
    plat.receiveShadow = true

    this._ownMeshes = [bumper, body, plat]
    // ownGroup이 center-origin ↔ bottom-up 좌표계 변환을 자동 처리
    for (const m of this._ownMeshes) this.components3D.add(m)

    // mesh 는 super.build() 이후 생성 → 코어 faint 를 명시 적용(편집 흐림이 mesh 에 닿게).
    this.updateAlpha()
  }

  updateDimension() {}

  update() {
    // 자신의 mesh만 제거 (Carrier 등 자식 컴포넌트 3D 보존)
    for (const m of this._ownMeshes) {
      this.components3D.remove(m)
      disposeObject3D(m)
    }
    this._ownMeshes = []

    this.build()
    this.updateTransform()
    this.updateAlpha()
    this.updateHidden()
  }

  // updateAlpha override 제거 — 코어 RealObject.updateAlpha(=applyMaterialOpacity)로 수렴.
  // canonical(bank base opacity 1) × state.alpha × faint. view 모드 불변, 편집 모드 faint 추가.

  onchange(after: Record<string, unknown>, before: Record<string, unknown>) {
    if ('width' in after || 'height' in after || 'depth' in after ||
        'fillStyle' in after || 'strokeStyle' in after || 'id' in after ||
        'EMPTYTYPE' in after || 'CARRIERSTATUS' in after) {
      this.update()
      return
    }

    super.onchange(after, before)
  }
}

registerRealObjectFactory('AGV', (component: Component) => new AGV3D(component))
